import { PinoModules, RootLogger } from "verdi-logging";
import { minuteMs } from "../timeConstants.js";
import { assertValidDiagnosticSampleRate } from "../utils/diagnosticsSampling.js";
import { parse, stringify } from "../utils/jsonSerializer/index.js";
import { type CacheDrilldown, type CacheOutcome, DIAGNOSTICS_OFF, emitCacheRead } from "./cacheDiagnostics.js";
import { CacheStoreUnavailableError } from "./cacheStoreUnavailableError.js";
import type { CacheActionOptions, CachedActionHandle, CacheStore, EncodedCacheValue } from "./types.js";

const KEY_NAMESPACE = "cachedAction";
const KEY_SEPARATOR = ":";
const KEY_RESERVED_CHARACTER_PATTERN = /[:*?[\]]/g;
const DEFAULT_FETCH_TIMEOUT_MS = 5 * minuteMs;

const logger = RootLogger.child({ module: PinoModules.caching });

/**
 * Percent-encodes a cache-key component. `encodeURIComponent` already escapes every character in
 * {@link KEY_RESERVED_CHARACTER_PATTERN} except `*` (it treats `*` as an unreserved URI mark), so
 * that's the only character this adds on top — no need to escape anything the reserved-character
 * check doesn't actually care about.
 */
export function encodeKeyComponent(value: string): string {
    return encodeURIComponent(value).replace(/\*/g, "%2A");
}

function defaultArgsToKey(args: unknown[]): string {
    for (let i = 0; i < args.length; i++) {
        if (typeof args[i] === "object" && args[i] !== null) {
            throw new Error(
                `Argument at index ${i} is an object. Provide a custom toKey function to avoid key collisions.`,
            );
        }
    }

    const stringifiedArgs = args.map(String);
    for (let i = 0; i < stringifiedArgs.length; i++) {
        const reservedCharacters = stringifiedArgs[i].match(KEY_RESERVED_CHARACTER_PATTERN);
        if (reservedCharacters) {
            logger.error(
                {
                    error: new Error("reserved cache-key characters detected"),
                    argumentIndex: i,
                    reservedCharacters: Array.from(new Set(reservedCharacters)),
                },
                "Argument contains key separators or illegal characters; encoder will percent-encode them",
            );
        }
    }

    return stringifiedArgs.map((arg) => encodeKeyComponent(arg)).join(KEY_SEPARATOR);
}

function buildFreshKey(actionType: string, argsKey: string): string {
    return `${KEY_NAMESPACE}:${actionType}:${argsKey}`;
}

function buildStaleKey(freshKey: string): string {
    return `${freshKey}:stale`;
}

function buildPersistKey(freshKey: string): string {
    return `${freshKey}:persist`;
}

type ParsedCacheValue<TResult> = { ok: true; value: TResult } | { ok: false };

// htmlScriptSafe is off: cache values are never embedded in HTML.
const defaultEncodeValue = (value: unknown): EncodedCacheValue => stringify(value, { htmlScriptSafe: false });
const defaultDecodeValue = <TResult>(value: string): TResult => parse(value) as TResult;

function guardEncodedValueForStore(encoded: EncodedCacheValue | undefined | null): string {
    if (encoded === undefined) {
        throw new Error("Encoded cache value is undefined");
    }
    if (encoded === null) {
        throw new Error("Encoded cache value is null");
    }
    if (typeof encoded === "string") {
        return encoded;
    }
    if (typeof encoded === "number") {
        if (!Number.isFinite(encoded)) {
            throw new Error(`Encoded cache value must be a finite number, received ${encoded}`);
        }
        return String(encoded);
    }
    throw new Error(`Encoded cache value must be a string or number, received ${typeof encoded}`);
}

function parseCachedValue<TResult>(
    value: string,
    source: "fresh" | "stale" | "persisted",
    actionType: string,
    argsKey: string,
    decodeValue: (value: string) => TResult,
): ParsedCacheValue<TResult> {
    try {
        return { ok: true, value: decodeValue(value) };
    } catch (error) {
        const fallback = source === "persisted" ? "rethrowing the fetch failure" : "falling back to fetch";
        logger.error({ error: error, actionType, argsKey }, `Failed to parse ${source} cache value; ${fallback}`);
        return { ok: false };
    }
}

function createFetchTimeoutError(actionType: string, timeoutMs: number): Error {
    return new Error(`Cached action "${actionType}" fetch timed out after ${timeoutMs}ms.`);
}

/**
 * Logs a failed store read. An unavailable store is a state, not an exception, so it drops to debug —
 * otherwise every cached read in an environment without a store emits an error line.
 */
function logStoreReadFailure(error: unknown, actionType: string, argsKey: string, message: string): void {
    const level = error instanceof CacheStoreUnavailableError ? "debug" : "error";
    logger[level]({ error: error, actionType, argsKey }, message);
}

type ActivatedFetch<TArgs extends unknown[], TResult> = (...args: TArgs) => Promise<TResult>;

function withFetchTimeout<TArgs extends unknown[], TResult>(
    fetch: ActivatedFetch<TArgs, TResult>,
    actionType: string,
    timeoutMs: number,
): (...args: TArgs) => Promise<TResult> {
    return async (...args: TArgs): Promise<TResult> => {
        let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

        const timeoutPromise = new Promise<never>((_resolve, reject) => {
            timeoutHandle = setTimeout(() => {
                reject(createFetchTimeoutError(actionType, timeoutMs));
            }, timeoutMs);
        });

        try {
            // Keep timeout handling local to each fetch call so callers just await one promise.
            return await Promise.race([fetch(...args), timeoutPromise]);
        } finally {
            // Always clear timer to avoid retaining handles after either branch settles.
            if (timeoutHandle !== undefined) {
                clearTimeout(timeoutHandle);
            }
        }
    };
}

export interface CacheContext {
    registerCachedAction<TArgs extends unknown[], TResult>(
        actionType: string,
        options: CacheActionOptions<TResult> & { toKey?: (...args: TArgs) => string },
    ): CachedActionHandle<TArgs, TResult>;
    bustAllCachedActions(): Promise<void>;
    /** Test-only: bypass all cache reads and writes. */
    disable(): void;
    /** Test-only: re-enable caching after a disable() call. */
    enable(): void;
    isDisabled(): boolean;
}

let _store: CacheStore | null = null;
let _disabled = false;
const _registeredActionTypes = new Set<string>();
const _inFlightCacheWrites = new Map<string, Promise<unknown>>();

async function awaitInFlightCacheWritesByPredicate(predicate: (freshKey: string) => boolean): Promise<void> {
    const pending = Array.from(_inFlightCacheWrites.entries())
        .filter(([freshKey]) => predicate(freshKey))
        .map(([, promise]) => promise);
    if (pending.length === 0) return;

    // In-flight fetches/revalidations handle their own errors, but invalidations should wait for them to settle.
    await Promise.allSettled(pending);
}

export function registerCacheStore(store: CacheStore): void {
    _store = store;
}

function getStore(): CacheStore {
    if (!_store) throw new Error("No cache store registered. Call registerCacheStore() during app startup.");
    return _store;
}

export const cacheContext: CacheContext = {
    registerCachedAction<TArgs extends unknown[], TResult>(
        actionType: string,
        {
            ttlMs,
            staleTtlMs,
            persistWhenUnavailableMs,
            fetchTimeoutMs = DEFAULT_FETCH_TIMEOUT_MS,
            diagnosticsSampleRate = DIAGNOSTICS_OFF,
            toKey,
            encodeValue = defaultEncodeValue,
            decodeValue = defaultDecodeValue,
        }: CacheActionOptions<TResult> & { toKey?: (...args: TArgs) => string },
    ): CachedActionHandle<TArgs, TResult> {
        // Global regexes are stateful with .test(); reset before each validation.
        KEY_RESERVED_CHARACTER_PATTERN.lastIndex = 0;
        if (KEY_RESERVED_CHARACTER_PATTERN.test(actionType)) {
            throw new Error(
                `Cached action type "${actionType}" may not include reserved characters used by cache keys ` +
                    `or invalidation patterns (${KEY_SEPARATOR}, *, ?, [, ]).`,
            );
        }
        if (_registeredActionTypes.has(actionType)) {
            throw new Error(`Cached action "${actionType}" has already been registered.`);
        }
        // The stale key always outlives a shorter persist key, so the persist tier would never be reached.
        if (persistWhenUnavailableMs !== undefined && persistWhenUnavailableMs <= (staleTtlMs ?? 0)) {
            throw new Error(
                `Cached action "${actionType}" has persistWhenUnavailableMs (${persistWhenUnavailableMs}ms) that does ` +
                    `not exceed staleTtlMs (${staleTtlMs ?? 0}ms); the persisted value would never be served.`,
            );
        }
        // 0 is the off switch here, so it skips a guard whose whole job is to reject it. Job queues keep that
        // guard: their diagnostics are on by default, so there a 0 is always a mistake.
        if (diagnosticsSampleRate !== DIAGNOSTICS_OFF)
            assertValidDiagnosticSampleRate(actionType, diagnosticsSampleRate);
        _registeredActionTypes.add(actionType);

        const serializeArgs = toKey ?? ((...args: TArgs) => defaultArgsToKey(args));
        let _fetch: ActivatedFetch<TArgs, TResult> | null = null;

        function getFetch(): ActivatedFetch<TArgs, TResult> {
            if (!_fetch)
                throw new Error(
                    `Cached action "${actionType}" has not been activated. Call .activate(fetch) before use.`,
                );
            return _fetch;
        }

        async function bustImpl(...args: TArgs): Promise<void> {
            if (_disabled) return;
            const argsKey = serializeArgs(...args);
            const freshKey = buildFreshKey(actionType, argsKey);
            const keys = [freshKey];
            if (staleTtlMs !== undefined) keys.push(buildStaleKey(freshKey));
            // A bust means the value is known-wrong, so drop the downtime copy too rather than serve it later.
            if (persistWhenUnavailableMs !== undefined) keys.push(buildPersistKey(freshKey));
            // Block until in-flight fetch/revalidation for this key settles to avoid post-bust repopulation.
            await awaitInFlightCacheWritesByPredicate((inFlightFreshKey) => inFlightFreshKey === freshKey);
            await getStore().deleteMany(keys);
        }

        return {
            activate(fetch: ActivatedFetch<TArgs, TResult>): void {
                _fetch = fetch;
            },

            async get(...args: TArgs): Promise<TResult> {
                const fetch = getFetch();
                // Apply timeout consistently for direct fetches and background revalidation fetches.
                const fetchWithTimeout = withFetchTimeout(fetch, actionType, fetchTimeoutMs);
                if (_disabled) return fetchWithTimeout(...args);

                const store = getStore();
                const argsKey = serializeArgs(...args);
                const freshKey = buildFreshKey(actionType, argsKey);

                const startedAt = Date.now();
                const drilldown: CacheDrilldown = { argsKey };
                // Both are read only by sourceOutcome(), to tell a degraded source read from a cold one.
                let cacheUnavailable = false;
                let servedFromPersist = false;

                const emit = (outcome: CacheOutcome) =>
                    emitCacheRead({ actionType, outcome, startedAt, sampleRate: diagnosticsSampleRate, drilldown });

                try {
                    let cached: string | null;
                    try {
                        cached = await store.get(freshKey);
                    } catch (error) {
                        // Read errors should not block callers. Treat an unreadable cache as a miss rather than a
                        // bare fetch, so the fetched value still repopulates every key: skipping the write would
                        // leave the persist copy older than a value already returned to callers.
                        cacheUnavailable = true;
                        drilldown.cacheReadError = "fresh";
                        logStoreReadFailure(
                            error,
                            actionType,
                            argsKey,
                            "Failed to read fresh cache key; treating as a cache miss",
                        );
                        return await readFromSource();
                    }
                    if (cached !== null) {
                        const parsedFresh = parseCachedValue<TResult>(
                            cached,
                            "fresh",
                            actionType,
                            argsKey,
                            decodeValue,
                        );
                        if (parsedFresh.ok) {
                            emit("cacheHit");
                            return parsedFresh.value;
                        }
                        // Say why, or an undecodable payload looks like a cold key.
                        drilldown.decodeError = "fresh";
                    }

                    if (staleTtlMs !== undefined) {
                        let stale: string | null;
                        try {
                            stale = await store.get(buildStaleKey(freshKey));
                        } catch (error) {
                            cacheUnavailable = true;
                            drilldown.cacheReadError = "stale";
                            logStoreReadFailure(
                                error,
                                actionType,
                                argsKey,
                                "Failed to read stale cache key; proceeding to direct fetch",
                            );
                            stale = null;
                        }
                        if (stale !== null) {
                            const parsedStale = parseCachedValue<TResult>(
                                stale,
                                "stale",
                                actionType,
                                argsKey,
                                decodeValue,
                            );
                            if (parsedStale.ok) {
                                // Deduplicate refreshes so concurrent stale reads share one background revalidation.
                                getOrCreateInFlightWrite({ logBackgroundFailure: true });
                                emit("cacheHitStale");
                                // Return stale immediately; refresh completion is intentionally decoupled.
                                return parsedStale.value;
                            }
                            drilldown.decodeError = "stale";
                        }
                    }

                    return await readFromSource();
                } catch (error) {
                    // Never the tier the read got through first: producing no value at all is `failed`, and a
                    // cacheMiss* outcome here would claim the source answered. The drill-down keeps how far it got.
                    emit("failed");
                    throw error;
                }

                /** The one exit that goes to the source, so its three possible outcomes emit in one place. */
                async function readFromSource(): Promise<TResult> {
                    const value = await withPersistFallback(() => getOrCreateInFlightWrite());
                    emit(sourceOutcome());
                    return value;
                }

                /** The persist tier answering is a hit; a failed store read makes the miss a degraded one. */
                function sourceOutcome(): CacheOutcome {
                    if (servedFromPersist) return "cacheHitSourceUnavailable";
                    return cacheUnavailable ? "cacheMissCacheUnavailable" : "cacheMiss";
                }

                /** Fetches from source and writes fresh (+ stale, + persist) keys. Write failures are logged, not thrown. */
                async function fetchAndCache(): Promise<TResult> {
                    const result = await fetchWithTimeout(...args);
                    try {
                        const serialized = guardEncodedValueForStore(encodeValue(result));
                        await store.set(freshKey, serialized, ttlMs);
                        if (staleTtlMs !== undefined) {
                            await store.set(buildStaleKey(freshKey), serialized, ttlMs + staleTtlMs);
                        }
                        if (persistWhenUnavailableMs !== undefined) {
                            await store.set(buildPersistKey(freshKey), serialized, ttlMs + persistWhenUnavailableMs);
                        }
                    } catch (error) {
                        // Write failures are intentionally non-fatal: callers should still get fresh data.
                        logger.error({ error: error, actionType, argsKey }, "Failed to cache fetched result");
                    }
                    return result;
                }

                /**
                 * Returns an existing in-flight fetch/write for this key, or starts one.
                 * `logBackgroundFailure` swallows rejection for fire-and-forget stale revalidation.
                 */
                function getOrCreateInFlightWrite(options?: { logBackgroundFailure?: boolean }): Promise<TResult> {
                    const existing = _inFlightCacheWrites.get(freshKey);
                    if (existing !== undefined) {
                        // Joined someone else's fetch, so the fetch time is not this caller's doing.
                        drilldown.deduped = true;
                        return existing as Promise<TResult>;
                    }

                    const inflightPromise = fetchAndCache().finally(() => _inFlightCacheWrites.delete(freshKey));
                    _inFlightCacheWrites.set(freshKey, inflightPromise);
                    if (options?.logBackgroundFailure) {
                        // Stale callers return immediately; swallow rejection so it is not unhandled.
                        void inflightPromise.catch((error) =>
                            logger.error({ error: error, actionType, argsKey }, "Background stale revalidation failed"),
                        );
                    }
                    return inflightPromise;
                }

                /** Reads the downtime copy. Not ok on store error, miss, or undecodable payload. */
                async function readPersisted(): Promise<ParsedCacheValue<TResult>> {
                    let persisted: string | null;
                    try {
                        persisted = await store.get(buildPersistKey(freshKey));
                    } catch (error) {
                        // Distinct key so an earlier cacheReadError survives.
                        drilldown.persistedReadFailed = true;
                        logStoreReadFailure(
                            error,
                            actionType,
                            argsKey,
                            "Failed to read persisted cache key while the source was unavailable",
                        );
                        return { ok: false };
                    }
                    if (persisted === null) return { ok: false };
                    return parseCachedValue<TResult>(persisted, "persisted", actionType, argsKey, decodeValue);
                }

                /**
                 * Runs a fetch attempt, serving the persisted copy if the source errors or times out.
                 * Rethrows the original fetch error when there is nothing to fall back to.
                 *
                 * The persist read trails the fetch, so a bust landing in between can be missed and the pre-bust
                 * value served. Unguarded: a bust from another instance is unobservable here anyway.
                 */
                async function withPersistFallback(attempt: () => Promise<TResult>): Promise<TResult> {
                    if (persistWhenUnavailableMs === undefined) return attempt();
                    try {
                        return await attempt();
                    } catch (error) {
                        const persisted = await readPersisted();
                        if (!persisted.ok) throw error;
                        servedFromPersist = true;
                        logger.warn(
                            { error: error, actionType, argsKey },
                            "Source unavailable; serving persisted value",
                        );
                        return persisted.value;
                    }
                }
            },

            async getFromSource(...args: TArgs): Promise<TResult> {
                const fetchWithTimeout = withFetchTimeout(getFetch(), actionType, fetchTimeoutMs);
                if (_disabled) return fetchWithTimeout(...args);

                // Never reads or writes any cache tier, and never joins an in-flight fetch: for callers that
                // must see a just-committed write — the consent gates — and must never be handed a cached or
                // pre-write value. The cache is left exactly as it was, so this neither warms nor
                // invalidates anything. Every other caller wants get().
                // Diagnostics metadata, not a precondition: no key is used to read or write anything here, so
                // args that cannot be serialized (an object with no toKey) must not stop the source read this
                // method exists to perform. Omitted from the drill-down when it fails, as in bestEffortBust.
                let argsKey: string | undefined;
                try {
                    argsKey = serializeArgs(...args);
                } catch {
                    // Reported without it rather than not at all.
                }

                const startedAt = Date.now();
                const emit = (outcome: CacheOutcome) =>
                    emitCacheRead({
                        actionType,
                        outcome,
                        startedAt,
                        sampleRate: diagnosticsSampleRate,
                        drilldown: argsKey !== undefined ? { argsKey } : {},
                    });

                try {
                    const value = await fetchWithTimeout(...args);
                    emit("sourceForced");
                    return value;
                } catch (error) {
                    emit("failed");
                    throw error;
                }
            },

            async bust(...args: TArgs): Promise<void> {
                return bustImpl(...args);
            },

            async bestEffortBust(...args: TArgs): Promise<void> {
                try {
                    await bustImpl(...args);
                } catch (error) {
                    let argsKey: string | undefined;
                    try {
                        argsKey = serializeArgs(...args);
                    } catch {
                        // Omit argsKey when serialization itself failed (the likely cause of the bust error).
                    }
                    logger.warn(
                        { error: error, actionType, ...(argsKey !== undefined ? { argsKey } : {}) },
                        "Cached action cache bust failed",
                    );
                }
            },

            async bustAll(): Promise<void> {
                if (_disabled) return;
                logger.info({ actionType }, "Busting all cached action entries");
                const actionPrefix = `${KEY_NAMESPACE}:${actionType}:`;
                await awaitInFlightCacheWritesByPredicate((inFlightFreshKey) =>
                    inFlightFreshKey.startsWith(actionPrefix),
                );
                await getStore().deleteAll(`${KEY_NAMESPACE}:${actionType}:*`);
            },
        };
    },

    async bustAllCachedActions(): Promise<void> {
        if (_disabled) return;
        logger.info("Busting all cached actions across all action types");
        await awaitInFlightCacheWritesByPredicate(() => true);
        await getStore().deleteAll(`${KEY_NAMESPACE}:*`);
    },

    disable(): void {
        if (process.env.NODE_ENV !== "test") throw new Error("disable() may only be called in the test environment.");
        _disabled = true;
    },

    enable(): void {
        if (process.env.NODE_ENV !== "test") throw new Error("enable() may only be called in the test environment.");
        _disabled = false;
    },

    isDisabled(): boolean {
        return _disabled;
    },
};
